<template>
  <div 
    v-if="isTimelineShown"
    class="flex justify-between items-center px-4 rounded-xl bg-white versions-timeline"
    data-testid="versions-timeline-container"
  >
    <div class="flex gap-4">
      <p
        class="w-28"
        data-testid="versions-timeline-label"
      >
        {{ $t('profile_view.versions.timeline.label') }}
      </p>
      <button
        v-if="hasItems"
        type="button"
        @click="scrollItems('left')"
      >
        <ArrowIcon />
      </button>
    </div>

    <div class="mx-4 overflow-hidden relative">
      <div class="versions-timeline__line pointer-events-none" />
      <div ref="scrollableContent" class="versions-timeline__scrollable-content">
        <Timeline
          v-if="hasItems"
          :list="translatedTimelinePoints"
          :readonly="isVersionsLoading"
          :disableCheckboxHandler="isSelectedPointsOnLimit"
          @select="onPointCLick"
        />
      </div>
    </div>

    <div class="flex items-center gap-4">
      <button
        v-if="hasItems"
        type="button"
        @click="scrollItems('right')"
      >
        <ArrowIcon class="rotate" />
      </button>

      <button
        type="button"
        :disabled="isComparisonModeButtonDisabled"
        class="flex justify-center items-center w-8 h-8 rounded-lg"
        :class="{
          'bg-primary': !isComparisonModeButtonDisabled,
          'bg-base-content-300 cursor-not-allowed': isComparisonModeButtonDisabled
        }"
        data-testid="versions-timeline-comparison-button"
        @click="toggleComparisonMode"
      >
        <SearchIcon
          v-if="!isComparisonMode"
          data-testid="versions-timeline-comparison-search-icon"
        />
        <font-awesome-icon
          v-else
          icon="times"
          size="lg"
          color="#fff"
          data-testid="versions-timeline-comparison-close-icon"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import Timeline from "./Timeline.vue";
import ArrowIcon from "../icons/arrow.svg";
import SearchIcon from "../icons/search.svg";
import permissionsMixin from "../mixins/permissions";
import { getLocalDateFormat, MMM_d_yyyy } from "@shared/utils/dateTime";
import { SCROLL_AMOUNT, SELECTED_POINTS_LIMIT } from "../constants";

export default {
  name: "VersionsTimelineContainer",

  mixins: [permissionsMixin],

  components: {
    Timeline,
    ArrowIcon,
    SearchIcon,
  },

  props: {
    formId: {
      type: String,
      required: true,
    },
  },

  beforeDestroy() {
    this.clearState();
  },

  computed: {
    ...mapGetters({
      isComparisonMode: "VersionsStore/getIsComparisonMode",
      timelinePoints: "VersionsStore/getTimelinePoints",
      selectedPoints: "VersionsStore/getSelectedPoints",
      isTimelineLoading: "VersionsStore/getIsTimelineLoading",
      isVersionsLoading: "VersionsStore/getIsVersionsLoading",
    }),

    translatedTimelinePoints() {
      return this.timelinePoints.map(el => ({
        ...el,
        date: el.is_current
          ? `(${this.$t('profile_view.versions.timeline.current')}) ${this.formatDate(el.date)}`
          : this.formatDate(el.date),
      }));
    },

    caseId() {
      return this.$route.query.caseid;
    },

    caseEntityId() {
      return this.$route.query.entityId;
    },

    isTimelineShown() {
      return (!this.isTimelineLoading && this.timelinePoints.length >= SELECTED_POINTS_LIMIT)
        && this.permissions.view_refresh_timeline;
    },

    isComparisonModeButtonDisabled() {
      return !this.hasItems || !this.isSelectedPointsOnLimit || this.isVersionsLoading;
    },

    isSelectedPointsOnLimit() {
      return this.selectedPoints.length === SELECTED_POINTS_LIMIT;
    },

    hasItems() {
      return this.timelinePoints.length > 0;
    },
  },

  methods: {
    ...mapActions({
      fetchComparedForms: "VersionsStore/fetchComparedForms",
      selectPoint: "VersionsStore/selectPoint",
      clearSelectedPoints: "VersionsStore/clearSelectedPoints",
      resetState: "VersionsStore/resetState",
      clearState: "VersionsStore/clearState"
    }),
    ...mapMutations({
      SET_COMPARISON_MODE: "VersionsStore/SET_COMPARISON_MODE",
    }),

    async toggleComparisonMode() {
      if (this.isComparisonMode) {
        this.resetState();
        return;
      }

      this.SET_COMPARISON_MODE(true);

      await this.fetchComparedForms({
        case_id: this.caseId,
        case_entity_id: this.caseEntityId,
        tenant_check_id: this.formId,
      });
    },

    onPointCLick(point) {
      if (this.isSelectedPointsOnLimit && !point.current) {
        this.$toast.error(this.$t('profile_view.versions.errors.two_dates'));
        return;
      }

      this.selectPoint(point);

      if (this.selectedPoints.length < SELECTED_POINTS_LIMIT && this.isComparisonMode) {
        this.SET_COMPARISON_MODE(false);
      }
    },

    scrollItems(direction) {
      this.$refs?.scrollableContent.scrollBy({
        left: direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
        behavior: "smooth",
      });
    },

    formatDate(date) {
      return getLocalDateFormat(date, { format: MMM_d_yyyy });
    },
  },
}
</script>

<style scoped lang="scss">
.versions-timeline {
  &__line {
    position: absolute;
    z-index: 1;
    top: 10px;
    left: 0;
    transform: translateY(-50%);
    width: 100%;
    height: 2px;
    background-color: var(--theme-color-text-300);
    opacity: 0.8;
  }

  &__scrollable-content {
    overflow-x: auto;
    white-space: nowrap;
    scroll-behavior: smooth;
  }

  &__scrollable-content::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Webkit browsers */ // TODO: Firefox
  }

  .rotate {
    transform: rotateZ(180deg);
  }
}
</style>
